import { Text } from '@components';
import { useEffect, useState } from 'react';
import { AsyncTypeahead, Highlighter } from 'react-bootstrap-typeahead';
import { Api } from '@services';

interface LocationSearchProps {
  setLatitude: (lat: number | null) => void;
  setLongitude: (lon: number | null) => void;
  setLocConfirm: (confirm: boolean) => void;
}

// Helper to determine Azure Maps domain directly from host context
const getAzureMapsDomain = () => {
  const hostname = window.location.hostname.toLowerCase();
  const isGov =
    hostname.endsWith('.gov') || hostname.includes('azure-gov') || hostname.includes('usgov');
  return isGov ? 'atlas.azure.us' : 'atlas.microsoft.com';
};

export function LocationSearch({ setLatitude, setLongitude, setLocConfirm }: LocationSearchProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<any[]>([]);
  const [cache, setCache] = useState({});
  const [query, setQuery] = useState<string>('');
  const [mapKey, setMapKey] = useState<string | null>(null);

  // Fetch Azure Maps Key directly from API
  useEffect(() => {
    Api.get(`/me/map-server-key`)
      .then((resp) => {
        setMapKey(resp.data as string);
      })
      .catch((err) => {
        console.error('Failed to fetch map key:', err);
      });
  }, []);

  const getResults = async (url: string) => {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Azure search failed with status ${response.status}`);
    }

    const data = await response.json();
    const azMapsDomain = getAzureMapsDomain();

    const next = `https://${azMapsDomain}/search/poi/json?subscription-key=${mapKey}&api-version=1.0&query=${
      data.summary.query
    }&limit=15&ofs=${data.summary.offset + 10}`;

    const total = data.summary.totalResults;

    const newItems = data.results.map((item: any) => ({
      name: item.address.freeformAddress,
      country: item.address.country,
      countrySubdivision: item.address.countrySubdivisionName,
      position: item.position,
    }));

    return { newItems, next, total };
  };

  const handleSearch = (value: string) => {
    setQuery(value);

    // If cached query items are available, set those as initial options
    if (cache[value]) {
      setOptions(cache[value].items);
    } else if (mapKey) {
      const azMapsDomain = getAzureMapsDomain();
      const initCall = `https://${azMapsDomain}/search/address/json?subscription-key=${mapKey}&api-version=1.0&query=${encodeURIComponent(
        value,
      )}&limit=15&ofs=0`;

      setIsLoading(true);
      getResults(initCall)
        .then((resp) => {
          setOptions(resp.newItems);
          setCache({
            ...cache,
            [value]: { items: resp.newItems, next: resp.next, total: resp.total },
          });
          setIsLoading(false);
        })
        .catch((err) => {
          console.error('Location search failed:', err);
          setIsLoading(false);
        });
    }
  };

  const handlePagination = () => {
    // If query cache length is less than total, continue fetching on pagination using cached next call
    if (cache[query] && cache[query].items.length !== cache[query].total) {
      setIsLoading(true);
      getResults(cache[query].next)
        .then((resp) => {
          const items = cache[query].items.concat(resp.newItems);
          setOptions(items);
          setCache({
            ...cache,
            [query]: { items: items, next: resp.next, total: resp.total },
          });
          setIsLoading(false);
        })
        .catch((err) => {
          console.error('Pagination fetch failed:', err);
          setIsLoading(false);
        });
    }
  };

  const handleLocation = (selected: any[]) => {
    if (selected.length > 0) {
      setLatitude(selected[0].position.lat);
      setLongitude(selected[0].position.lon);
      setLocConfirm(true);
    } else {
      setLatitude(null);
      setLongitude(null);
    }
  };

  return (
    <>
      <Text>Location:</Text>
      <AsyncTypeahead
        className={'w-100'}
        delay={500}
        id="identifier"
        isLoading={isLoading}
        inputProps={{ style: { width: '100%' } }}
        onPaginate={handlePagination}
        maxResults={10}
        minLength={1}
        filterBy={() => true}
        options={options.map((option) => {
          return {
            label: option.name,
            country: option.country,
            countrySubdivision: option.countrySubdivision,
            position: option.position,
          };
        })}
        onSearch={handleSearch}
        onChange={handleLocation}
        paginate
        placeholder="Search for a location to place this map"
        renderMenuItemChildren={(option, props) => (
          <div title={option.label}>
            <Highlighter search={props.text}>{option.label}</Highlighter>
            <div>
              <small className="filter-sm-name">{option.country}</small>
              <small className="filter-sm-name"> - {option.countrySubdivision}</small>
            </div>
          </div>
        )}
        useCache={false}
      />
      <div className="search-placeholder" />
    </>
  );
}
